import React, { useState, useRef } from 'react';
import { Droplet, Filter, Wind, CheckCircle, Shield, Fan, Lock, Crown } from 'lucide-react';
import { translations } from '../translations';
import '../styles/MaintenanceGrid.css';

const STYLES = `
@keyframes panelOpen {
  0%   { opacity: 0; transform: translateY(-12px) scaleY(0.85); }
  60%  { transform: translateY(2px) scaleY(1.02); }
  100% { opacity: 1; transform: translateY(0) scaleY(1); }
}
@keyframes thumbDrop {
  0%   { opacity: 0; transform: translate(-50%, -70%) scale(0.3); }
  65%  { transform: translate(-50%, -50%) scale(1.25); }
  100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}
@keyframes pctFadeIn {
  0%   { opacity: 0; transform: translateY(6px); }
  100% { opacity: 1; transform: translateY(0); }
}
@keyframes cardPulse {
  0%   { box-shadow: 0 0 0 0px var(--pulse-color); }
  100% { box-shadow: 0 0 0 7px transparent; }
}
@keyframes mRipple {
  0%   { transform: scale(0); opacity: 0.45; }
  100% { transform: scale(3.5); opacity: 0; }
}
.m-ripple {
  position: absolute;
  border-radius: 50%;
  background: rgba(150,150,150,0.3);
  width: 50px; height: 50px;
  margin-left: -25px; margin-top: -25px;
  pointer-events: none;
  animation: mRipple 0.55s ease-out both;
}
.maintenance-card.m-selected {
  background: rgba(0,0,0,0.06) !important;
  border-color: rgba(0,0,0,0.12) !important;
  box-shadow: none !important;
}
.m-panel {
  margin-top: 0.75rem;
  border-radius: 16px;
  padding: 1rem 1rem 0.85rem;
  border: 1.5px solid;
  transform-origin: top center;
  animation: panelOpen 0.38s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.m-adj-btn {
  width: 30px; height: 30px;
  border-radius: 8px;
  border: 1.5px solid;
  background: transparent;
  font-size: 1.1rem; font-weight: 700;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
  line-height: 1;
}
.m-adj-btn:active { transform: scale(0.9); }
`;

const MaintenanceGrid = ({ t, expenses = [], car, distanceUnit = 'km', isPro, onProClick, currentMileageKm }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [ripples, setRipples] = useState([]);
  const [showAdjust, setShowAdjust] = useState(false);
  // intervalOverrides: { [itemId]: kmOverride }  — user-adjustable interval
  const [intervalOverrides, setIntervalOverrides] = useState({});
  const rippleId = useRef(0);

  // ── Mileage calculations ────────────────────────────────────────────────────
  const isMiles = distanceUnit === 'miles' || distanceUnit === 'mi';

  // Use the authoritative value passed from App.jsx (same source as CarCard) when available.
  let currentMileage;
  if (currentMileageKm != null) {
    // currentMileageKm is always in km — convert to display unit if needed
    currentMileage = isMiles ? Math.round(currentMileageKm * 0.621371) : currentMileageKm;
  } else {
    // Legacy fallback
    const baseMileageRaw = parseInt(car?.mileage || 0, 10);
    const gpsHistoricalKm = car?.total_mileage_km || 0;
    const totalKm = baseMileageRaw + Math.round(gpsHistoricalKm);
    currentMileage = isMiles ? Math.round(totalKm * 0.621371) : totalKm;
  }


  const getDaysRemaining = (lastDateStr, monthsInterval, customExpiryStr) => {
    let dueDate;
    if (customExpiryStr) {
      dueDate = new Date(customExpiryStr);
    } else {
      const d = new Date(lastDateStr);
      dueDate = new Date(d.setMonth(d.getMonth() + monthsInterval));
    }
    return Math.ceil((dueDate - new Date()) / 86400000);
  };

  // Returns status + conditionPct + raw km data for the panel
  const getStatus = (categoryId, baseKmInterval, monthsInterval, warningKmLeft = 2000, warningDaysLeft = 60) => {
    // Apply user override if set
    const kmInterval = intervalOverrides[categoryId] != null ? intervalOverrides[categoryId] : baseKmInterval;

    let categoryKeys = [];
    switch (categoryId) {
      case 'oil': categoryKeys = Object.values(translations).map(l => l.categories?.oilChange).filter(Boolean); break;
      case 'oilFilter': categoryKeys = Object.values(translations).map(l => l.categories?.oilFilter).filter(Boolean); break;
      case 'airFilter': categoryKeys = Object.values(translations).map(l => l.categories?.airFilter).filter(Boolean); break;
      case 'cabinFilter': categoryKeys = Object.values(translations).map(l => l.categories?.cabinFilter).filter(Boolean); break;
      case 'inspection': categoryKeys = Object.values(translations).map(l => l.categories?.carInspection).filter(Boolean); break;
      case 'insurance': categoryKeys = Object.values(translations).map(l => l.categories?.insurance).filter(Boolean); break;
      default: return null;
    }

    const noData = {
      display: kmInterval ? `-- ${distanceUnit}` : '--',
      isOverdue: false, isWarning: false, conditionPct: 0,
      kmDriven: null, kmInterval, baseKmInterval,
    };
    if (!car) return noData;

    const hits = expenses.filter(e => categoryKeys.includes(e.category));
    if (hits.length === 0) return noData;
    hits.sort((a, b) => new Date(b.date) - new Date(a.date));
    const latest = hits[0];

    // Time dimension
    const daysLeft = getDaysRemaining(latest.date, monthsInterval, latest.expiryDate);
    const totalDays = monthsInterval * 30;
    const timePct = Math.min(100, Math.max(0, ((totalDays - daysLeft) / totalDays) * 100));
    const isTimeOverdue = daysLeft < 0;
    const isTimeWarn = !isTimeOverdue && daysLeft <= warningDaysLeft;

    let timeDisplay = daysLeft < 0
      ? (t?.overdueByDays ? t.overdueByDays(Math.abs(daysLeft)) : `Overdue by ${Math.abs(daysLeft)}d`)
      : daysLeft < 30
        ? (t?.daysLeft ? t.daysLeft(daysLeft) : `${daysLeft}d left`)
        : (t?.monthsLeft ? t.monthsLeft(Math.floor(daysLeft / 30)) : `${Math.floor(daysLeft / 30)}mo left`);

    // Distance dimension
    let distanceDisplay = null, isDistOverdue = false, isDistWarn = false, distPct = 0, kmDriven = null;
    if (kmInterval) {
      if (latest.mileage != null) {
        const latestMileageConverted = isMiles ? Math.round(latest.mileage * 0.621371) : latest.mileage;
        kmDriven = Math.max(0, currentMileage - latestMileageConverted);
        const left = kmInterval - kmDriven;
        isDistOverdue = left < 0;
        isDistWarn = !isDistOverdue && left <= warningKmLeft;
        distPct = Math.min(100, Math.max(0, (kmDriven / kmInterval) * 100));
        distanceDisplay = isDistOverdue
          ? (t?.overdueByDist ? t.overdueByDist(Math.abs(left).toLocaleString(), distanceUnit) : `Overdue by ${Math.abs(left).toLocaleString()}${distanceUnit}`)
          : (t?.distLeft ? t.distLeft(left.toLocaleString(), distanceUnit) : `${left.toLocaleString()}${distanceUnit} left`);
      } else {
        distanceDisplay = t?.noDistData ? t.noDistData(distanceUnit) : `-- ${distanceUnit} left`;
      }
    }

    const isOverdue = isTimeOverdue || isDistOverdue;
    const isWarning = !isOverdue && (isTimeWarn || isDistWarn);
    const conditionPct = Math.max(timePct, distPct);
    const barPct = (kmInterval && latest.mileage != null) ? distPct : timePct;
    const display = distanceDisplay ? `${timeDisplay} • ${distanceDisplay}` : timeDisplay;

    return { display, isOverdue, isWarning, conditionPct, barPct, kmDriven, kmInterval, baseKmInterval };
  };

  const kmToUnit = (km) => (distanceUnit === 'miles' || distanceUnit === 'mi') ? Math.round(km * 0.621371) : km;

  // Default intervals (base, before overrides)
  const BASE_INTERVALS = {
    oil: isMiles ? 7000 : 12000,
    oilFilter: isMiles ? 7000 : 12000,
    airFilter: isMiles ? 18500 : 30000,
    cabinFilter: isMiles ? 12500 : 20000,
  };
  const STEP = isMiles ? 500 : 1000; // +/- per press

  const maintenanceItems = [
    { id: 'oil', label: t?.categories?.oilChange || 'Oil Change', icon: <Droplet size={22} />, status: getStatus('oil', BASE_INTERVALS.oil, 12, kmToUnit(4000), 60) },
    { id: 'oilFilter', label: t?.categories?.oilFilter || 'Oil Filter', icon: <Filter size={22} />, status: getStatus('oilFilter', BASE_INTERVALS.oilFilter, 12, kmToUnit(4000), 60) },
    { id: 'airFilter', label: t?.categories?.airFilter || 'Air Filter', icon: <Wind size={22} />, status: getStatus('airFilter', BASE_INTERVALS.airFilter, 12, kmToUnit(10000), 60) },
    { id: 'inspection', label: t?.categories?.carInspection || 'Car Inspection', icon: <CheckCircle size={22} />, status: getStatus('inspection', null, 12) },
    { id: 'insurance', label: t?.categories?.insurance || 'Insurance', icon: <Shield size={22} />, status: getStatus('insurance', null, 12) },
    { id: 'cabinFilter', label: t?.categories?.cabinFilter || 'Cabin Filter', icon: <Fan size={22} />, status: getStatus('cabinFilter', BASE_INTERVALS.cabinFilter, 12, kmToUnit(5000), 60) },
  ];

  // Zone thresholds: 0–80 green, 80–95 yellow, 95–100 red
  const getZone = (pct) => {
    if (pct < 80) return { label: t?.zoneExcellent || 'Excellent', color: '#16a34a', bg: 'rgba(22,163,74,0.12)', selColor: 'rgba(22,163,74,0.55)' };
    if (pct < 95) return { label: t?.zoneRecommended || 'Recommended', color: '#d97706', bg: 'rgba(217,119,6,0.12)', selColor: 'rgba(217,119,6,0.55)' };
    return { label: t?.zoneOverdue || 'Overdue', color: '#dc2626', bg: 'rgba(220,38,38,0.12)', selColor: 'rgba(220,38,38,0.55)' };
  };

  const expandedItem = maintenanceItems.find(i => i.id === expandedId);
  const expandedZone = expandedItem ? getZone(expandedItem.status?.conditionPct ?? 0) : null;

  const handleCardClick = (id, e) => {
    const rid = ++rippleId.current;
    const rect = e.currentTarget.getBoundingClientRect();
    setRipples(prev => [...prev, { id: rid, cardId: id, x: e.clientX - rect.left, y: e.clientY - rect.top }]);
    setTimeout(() => setRipples(prev => prev.filter(r => r.id !== rid)), 600);

    if (!isPro) {
      if (onProClick) onProClick();
      return;
    }

    setShowAdjust(false);
    setExpandedId(prev => prev === id ? null : id);
  };

  const adjustInterval = (id, delta) => {
    const base = BASE_INTERVALS[id] ?? kmToUnit(12000);
    const current = intervalOverrides[id] ?? base;
    const next = Math.max(STEP, current + delta);
    setIntervalOverrides(prev => ({ ...prev, [id]: next }));
  };

  return (
    <div className="maintenance-grid-container">
      <style>{STYLES}</style>
      <h3 className="section-title">{t?.categories?.maintenance || 'Maintenance'}</h3>

      {/* ── Card grid ── */}
      <div className="maintenance-grid">
        {maintenanceItems.map((item) => {
          const isSelected = expandedId === item.id;
          const zone = getZone(item.status?.conditionPct ?? 0);

          return (
            <div
              key={item.id}
              className={`maintenance-card${isSelected ? ' m-selected' : ''}`}
              onClick={(e) => handleCardClick(item.id, e)}
              style={{}}
            >
              {ripples.filter(r => r.cardId === item.id).map(r => (
                <span key={r.id} className="m-ripple" style={{ left: r.x, top: r.y }} />
              ))}
              <div className={`maintenance-icon-wrapper ${item.status?.isOverdue ? 'overdue-icon' : item.status?.isWarning ? 'warning-icon' : ''}`}
              >
                {item.icon}
              </div>
              {item.label && <span className="maintenance-label">{item.label}</span>}
              {item.status && (
                <span className={`maintenance-status ${item.status.isOverdue ? 'overdue-text' : item.status.isWarning ? 'warning-text' : ''}`}>
                  {item.status.display}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Expanded panel ── */}
      {expandedItem && expandedZone && (() => {
        const s = expandedItem.status;
        const barPct = s?.barPct ?? s?.conditionPct ?? 0;
        const hasKm = s?.kmInterval != null && s?.kmDriven != null;
        const isOverridden = intervalOverrides[expandedId] != null;

        return (
          <div
            key={expandedId}
            className="m-panel"
            style={{
              background: 'var(--bg-secondary, rgba(0,0,0,0.03))',
              borderColor: 'var(--border-color)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.85rem' }}>
              <div className={`maintenance-icon-wrapper ${s?.isOverdue ? 'overdue-icon' : s?.isWarning ? 'warning-icon' : ''}`}
                style={{ marginBottom: 0, flexShrink: 0, width: 38, height: 38 }}
              >
                {expandedItem.icon}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-primary)' }}>{expandedItem.label}</div>
                <div className={`maintenance-status ${s?.isOverdue ? 'overdue-text' : s?.isWarning ? 'warning-text' : ''}`} style={{ marginTop: 1 }}>
                  {s?.display}
                </div>
              </div>
              <span style={{
                fontSize: '0.65rem', fontWeight: 700, padding: '3px 9px', borderRadius: '20px',
                background: 'var(--bg-primary)', color: expandedZone.color,
                border: '1.5px solid var(--border-color)',
                animation: 'pctFadeIn 0.3s ease 0.2s both',
              }}>
                {expandedZone.label}
              </span>
            </div>

            {/* Label row above bar */}
            <div style={{ fontSize: '0.62rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.07em', opacity: 0.8 }}>
              {t?.conditionLabel || 'Condition — based on mileage & time'}
            </div>

            {/* km labels flanking bar */}
            {hasKm && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                <span style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                  0 {distanceUnit}
                </span>
                <span style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                  {s.kmInterval.toLocaleString()} {distanceUnit}
                </span>
              </div>
            )}

            {/* Gradient bar */}
            <div style={{ position: 'relative' }}>
              <div style={{
                position: 'relative', height: '18px', borderRadius: '10px',
                background: 'linear-gradient(90deg, #16a34a 0%, #16a34a 80%, #d97706 80%, #d97706 95%, #dc2626 95%, #dc2626 100%)',
                overflow: 'hidden', boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
              }}>
                {/* Dark mask */}
                <div style={{
                  position: 'absolute', top: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.42)',
                  width: `${100 - barPct}%`,
                  transition: 'width 0.9s cubic-bezier(0.22,1,0.36,1)',
                  borderRadius: '0 10px 10px 0',
                }} />
                {/* Dividers */}
                <div style={{ position: 'absolute', left: '80%', top: 0, bottom: 0, width: '1.5px', background: 'rgba(255,255,255,0.5)', zIndex: 2 }} />
                <div style={{ position: 'absolute', left: '95%', top: 0, bottom: 0, width: '1.5px', background: 'rgba(255,255,255,0.5)', zIndex: 2 }} />
              </div>

              {/* Thumb */}
              <div style={{
                position: 'absolute', left: `calc(${barPct}% + ${12 - barPct * 0.24}px)`, top: '50%',
                transform: 'translate(-50%, -50%)',
                width: '24px', height: '24px', borderRadius: '50%',
                background: 'white', border: `3px solid ${expandedZone.color}`,
                boxShadow: `0 2px 12px rgba(0,0,0,0.3), 0 0 0 4px ${expandedZone.color}30`,
                animation: 'thumbDrop 0.55s cubic-bezier(0.22,1,0.36,1) 0.12s both',
                zIndex: 4, pointerEvents: 'none',
              }} />

              {/* km driven below thumb */}
              {hasKm && (
                <div style={{
                  position: 'absolute', left: `calc(${barPct}% + ${12 - barPct * 0.24}px)`,
                  top: '100%', marginTop: '4px',
                  transform: 'translateX(-50%)',
                  fontSize: '0.58rem', fontWeight: 700,
                  color: 'var(--text-secondary)',
                  whiteSpace: 'nowrap',
                  animation: 'pctFadeIn 0.4s ease 0.5s both',
                }}>
                  {s.kmDriven.toLocaleString()} {distanceUnit}
                </div>
              )}
            </div>

            {/* Spacer for the floating km label */}
            {hasKm && <div style={{ height: '1.2rem' }} />}

            {/* ── Interval adjust button ── */}
            {hasKm && (
              <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAdjust(true); }}
                  style={{
                    fontSize: '0.7rem', fontWeight: 700,
                    padding: '5px 12px', borderRadius: '20px',
                    border: '1.5px solid var(--border-color)',
                    background: 'transparent', color: 'var(--text-secondary)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                  }}
                >
                  ⚙ {t?.adjustInterval || 'Adjust interval'}
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Interval adjust popup ── */}
      {showAdjust && expandedItem && expandedZone && (() => {
        const s = expandedItem.status;
        return (
          <div
            onClick={() => setShowAdjust(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backdropFilter: 'blur(4px)',
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: 'var(--bg-primary)',
                borderRadius: '20px',
                padding: '1.5rem',
                width: '280px',
                boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
                border: '1.5px solid var(--border-color)',
                animation: 'panelOpen 0.3s cubic-bezier(0.22,1,0.36,1) both',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.2rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--text-primary)' }}>{expandedItem.label}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 2 }}>{t?.serviceInterval || 'Service interval'}</div>
                </div>
                <button
                  onClick={() => setShowAdjust(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '1.2rem', lineHeight: 1, padding: 4 }}
                >
                  ✕
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'center' }}>
                <button
                  className="m-adj-btn"
                  style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', width: 40, height: 40, fontSize: '1.3rem', borderRadius: '12px' }}
                  onClick={() => adjustInterval(expandedId, -STEP)}
                >
                  −
                </button>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontWeight: 900, fontSize: '1.5rem', color: 'var(--text-primary)', lineHeight: 1 }}>
                    {(intervalOverrides[expandedId] ?? s.kmInterval ?? 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', marginTop: 3 }}>{distanceUnit}</div>
                </div>
                <button
                  className="m-adj-btn"
                  style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)', width: 40, height: 40, fontSize: '1.3rem', borderRadius: '12px' }}
                  onClick={() => adjustInterval(expandedId, +STEP)}
                >
                  +
                </button>
              </div>


            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default MaintenanceGrid;
